import { useCallback, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDebounce } from 'use-debounce'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Bot,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CornerUpRight,
  Flag,
  Mail,
  MailOpen,
  MessageSquare,
  Search,
  Send,
  Star,
  Trash2,
  User,
  UserMinus,
  X,
} from 'lucide-react'

import { ActionsDropdown } from 'components/Buttons/ActionsDropdown'
import confirm from 'components/dialogConfirm'
import { cn } from 'components/UI/cn'
import { StyledEditorRHF } from 'components/Editor/EditorRHF'
import { ReactCreatableSelectWrapper } from 'components/FormUtils/ReactCreatableSelectWrapper'
import {
  SectionedMultiSelect,
  SectionedSection,
} from 'components/UI/Select/SectionedMultiSelect'
import { useGetCampaigns } from 'features/campaigns/campaigns_api'
import { formatRelativeTime } from 'utils/formatting'
import {
  ForwardReplyPayload,
  Reply,
  ReplyFilters,
  ReplySummary,
  ReplyToReplyPayload,
  useForwardReply,
  useGetReplyThreadsInfinite,
  useGetReplyThreadSummary,
  useGetThreadReplies,
  useGetCampaignSequence,
  useGetWorkspaceSenderEmails,
  useDeleteReply,
  useMarkReplyAutomated,
  useMarkReplyInterested,
  useMarkReplyRead,
  useReplyToReply,
  useToggleThreadFlag,
  useUnsubscribeReplyContact,
} from 'features/campaigns/sequences_api'
import { useInfiniteScroll } from 'hooks/useInfiniteScroll'

interface Props {
  campaignId?: number
  className?: string
}

const READ_STATUS_SECTIONS: SectionedSection[] = [
  {
    key: 'read_status',
    items: [
      { label: 'Unread', value: 'unread' },
      { label: 'Read', value: 'read' },
    ],
  },
]

type Classification = 'all' | 'interested' | 'not_interested' | 'flagged'

const CLASSIFICATION_OPTIONS: {
  key: Classification
  label: string
  disabled?: boolean
}[] = [
  { key: 'all', label: 'All' },
  { key: 'interested', label: 'Interested' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'flagged', label: 'Flagged' },
]

function ClassificationPills({
  selected,
  onChange,
  summary,
}: {
  selected: Classification
  onChange: (value: Classification) => void
  summary?: ReplySummary
}) {
  const getCount = (key: Classification): number | undefined => {
    if (!summary) return undefined
    switch (key) {
      case 'all':
        return summary.total
      case 'interested':
        return summary.interested
      case 'not_interested':
        return summary.not_interested
      case 'flagged':
        return summary.flagged
      default:
        return undefined
    }
  }

  return (
    <div className="flex gap-1.5 flex-wrap">
      {CLASSIFICATION_OPTIONS.map(({ key, label, disabled }) => {
        const count = getCount(key)
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(key)}
            className={cn(
              'px-2.5 py-1 rounded-full text-xs transition-colors',
              disabled && 'opacity-40 cursor-not-allowed',
              selected === key
                ? 'bg-brand-hover text-fg-inverse'
                : 'bg-surface-alt text-fg-secondary hover:bg-surface-hover'
            )}
          >
            {label}
            {count !== undefined && ` (${count})`}
          </button>
        )
      })}
    </div>
  )
}

function ReplyListItem({
  reply,
  isSelected,
  onClick,
}: {
  reply: Reply
  isSelected: boolean
  onClick: () => void
}) {
  const displayName =
    reply.lead_full_name || reply.from_name || reply.from_email_address

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3.5 border-b border-surface-alt transition-colors',
        isSelected ? 'bg-surface-hover/60' : 'hover:bg-surface-raised'
      )}
    >
      {/* Row 1: unread dot + name + timestamp */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {!reply.is_read && (
            <span className="size-2 rounded-full bg-brand-hover shrink-0" />
          )}
          <span
            className={cn(
              'text-sm truncate',
              reply.is_read ? 'text-fg-secondary' : 'text-fg'
            )}
          >
            {displayName}
          </span>
          {reply.thread_count > 1 && (
            <span className="text-xxs text-fg-secondary shrink-0">
              ({reply.thread_count})
            </span>
          )}
        </div>
        <span className="text-xxs text-gray-400 shrink-0 ml-2">
          {reply.date_received ? formatRelativeTime(reply.date_received) : ''}
        </span>
      </div>

      {/* Row 2: company name */}
      {reply.lead_company && (
        <p className="text-xs text-gray-400 mb-0.5">{reply.lead_company}</p>
      )}

      {/* Row 3: body preview */}
      <p className="text-xs text-fg-secondary truncate mb-2">
        {reply.text_body}
      </p>

      {/* Row 4: badges + emails sent */}
      <div className="flex items-center gap-2 flex-wrap">
        {reply.is_interested === true && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-emerald-600 bg-emerald-50">
            <Star className="size-2.5" />
            Interested
          </span>
        )}
        {reply.is_interested === false && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-gray-600 bg-gray-100">
            <X className="size-2.5" />
            Not Interested
          </span>
        )}
        {reply.is_flagged && (
          <span className="inline-flex items-center gap-1 text-xxs px-1.5 py-0.5 rounded-full text-amber-600 bg-amber-50">
            <Flag className="size-2.5" />
            Flagged
          </span>
        )}
        <span className="text-xxs text-gray-400 ml-auto flex items-center gap-1">
          <Mail className="size-2.5" />
          {reply.emails_sent_count != null && reply.emails_sent_count > 0
            ? `${reply.emails_sent_count} sent`
            : 'No emails sent'}
        </span>
      </div>
    </button>
  )
}

function ReplyDetailHeader({
  reply,
  currentIndex,
  totalCount,
  onPrev,
  onNext,
}: {
  reply: Reply
  currentIndex: number
  totalCount: number
  onPrev: () => void
  onNext: () => void
}) {
  const isFirst = currentIndex === 0
  const isLast = currentIndex === totalCount - 1

  return (
    <div className="flex items-center gap-2 px-[20px] py-2 border-b border-line bg-surface-raised">
      <span className="inline-flex items-center text-xxs px-2 py-0.5 rounded-full bg-surface-hover text-brand-hover shrink-0">
        Step {reply.emails_sent_count || 1}
      </span>
      <span className="text-xs text-fg-secondary truncate min-w-0 flex-1">
        {reply.subject || '(no subject)'}
      </span>
      <span className="text-xs text-fg-secondary shrink-0 ml-2">
        {currentIndex + 1} of {totalCount}
      </span>
      <button
        type="button"
        disabled={isFirst}
        onClick={onPrev}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isFirst
            ? 'text-fg-muted cursor-not-allowed'
            : 'text-fg hover:bg-surface-hover'
        )}
      >
        <ChevronLeft className="size-4" />
      </button>
      <button
        type="button"
        disabled={isLast}
        onClick={onNext}
        className={cn(
          'size-7 inline-flex items-center justify-center rounded transition-colors shrink-0',
          isLast
            ? 'text-fg-muted cursor-not-allowed'
            : 'text-fg hover:bg-surface-hover'
        )}
      >
        <ChevronRight className="size-4" />
      </button>
    </div>
  )
}

function ReplyContactHeader({
  reply,
  trailing,
  showUnreadDot,
}: {
  reply: Reply
  trailing?: React.ReactNode
  showUnreadDot?: boolean
}) {
  const displayName =
    reply.from_name || reply.lead_full_name || reply.from_email_address

  return (
    <div className="px-[20px] pt-4 pb-3">
      <div className="flex items-start gap-3">
        <div className="size-10 rounded-full bg-black/5 flex items-center justify-center shrink-0">
          <User className="size-5 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-fg truncate">
              {displayName}{' '}
              <span className="text-gray-400">
                &lt;{reply.from_email_address}&gt;
              </span>
            </p>
            <div className="flex items-center gap-1.5 shrink-0 ml-3">
              {showUnreadDot && !reply.is_read && (
                <span className="size-2 rounded-full bg-brand-hover shrink-0" />
              )}
              <span className="text-xs text-fg-secondary">
                {reply.date_received
                  ? formatRelativeTime(reply.date_received)
                  : ''}
              </span>
              {trailing}
            </div>
          </div>
          {reply.lead_company && (
            <p className="text-xs text-brand-active">{reply.lead_company}</p>
          )}
          <p className="text-xs text-gray-400">
            to {reply.primary_to_email_address}
          </p>
        </div>
      </div>
    </div>
  )
}

function ReplyBodyText({ reply }: { reply: Reply }) {
  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-[20px] py-5">
      <div className="text-sm leading-[1.8] text-fg space-y-3">
        <p className="whitespace-pre-wrap">{reply.text_body}</p>
      </div>
    </div>
  )
}

function ReplyClassificationRow({
  reply,
  onToggleInterested,
  onToggleFlag,
}: {
  reply: Reply
  onToggleInterested: () => void
  onToggleFlag: () => void
}) {
  return (
    <div className="shrink-0 px-[20px] py-3 border-t border-line">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Classification:</span>
          <button
            type="button"
            onClick={onToggleInterested}
            className={cn(
              'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition-colors',
              reply.is_interested === true
                ? 'text-emerald-600 bg-emerald-50 hover:bg-emerald-100'
                : reply.is_interested === false
                  ? 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                  : 'text-gray-400 bg-surface-alt hover:bg-surface-hover'
            )}
          >
            {reply.is_interested === true ? (
              <>
                <Star className="size-3" />
                Interested
              </>
            ) : reply.is_interested === false ? (
              <>
                <X className="size-3" />
                Not Interested
              </>
            ) : (
              'Unclassified'
            )}
          </button>
        </div>
        <div className="w-px h-4 bg-line" />
        <button
          type="button"
          onClick={onToggleFlag}
          className={cn(
            'inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors',
            reply.is_flagged
              ? 'text-amber-600 border-amber-200 bg-amber-50'
              : 'text-gray-400 border-transparent hover:bg-surface-alt'
          )}
        >
          <Flag className="size-3" />
          {reply.is_flagged ? 'Flagged' : 'Flag for Follow-up'}
        </button>
      </div>
    </div>
  )
}

interface ReplyActions {
  onMarkRead: (replyId: number, isRead: boolean) => void
  onMarkAutomated: (replyId: number, isAutomated: boolean) => void
  onUnsubscribe: (replyId: number) => void
  onDelete: (replyId: number) => void
}

function ThreadMessage({
  reply,
  defaultExpanded,
  actions,
}: {
  reply: Reply
  defaultExpanded: boolean
  actions: ReplyActions
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const handleToggle = () => {
    const willExpand = !expanded
    setExpanded(willExpand)
    if (willExpand && !reply.is_read) {
      actions.onMarkRead(reply.id, true)
    }
  }

  const menuActions = useMemo(
    () => [
      {
        label: reply.is_read ? 'Mark unread' : 'Mark read',
        icon: reply.is_read ? MailOpen : Mail,
        onClick: () => actions.onMarkRead(reply.id, !reply.is_read),
      },
      {
        label: reply.is_automated_reply
          ? 'Mark not automated'
          : 'Mark automated',
        icon: Bot,
        onClick: () =>
          actions.onMarkAutomated(reply.id, !reply.is_automated_reply),
      },
      {
        label: 'Unsubscribe contact',
        icon: UserMinus,
        onClick: () => actions.onUnsubscribe(reply.id),
        hasSeparatorAfter: true,
      },
      {
        label: 'Delete reply',
        icon: Trash2,
        onClick: () => actions.onDelete(reply.id),
        variant: 'destructive' as const,
      },
    ],
    [reply, actions]
  )

  return (
    <div className="border-b border-line">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full text-left group"
      >
        <ReplyContactHeader
          reply={reply}
          showUnreadDot
          trailing={
            <>
              {expanded && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <ActionsDropdown
                    actions={menuActions}
                    className="size-6 inline-flex items-center justify-center rounded hover:bg-surface-alt transition-colors text-fg-secondary"
                  />
                </div>
              )}
              <ChevronDown
                className={cn(
                  'size-4 text-fg-muted transition-transform group-hover:text-fg-secondary',
                  !expanded && '-rotate-90'
                )}
              />
            </>
          }
        />
      </button>
      {!expanded && (
        <button
          type="button"
          onClick={handleToggle}
          className="w-full text-left"
        >
          <p className="text-xs text-fg-secondary truncate px-[20px] pb-3 pl-[68px] -mt-1">
            {reply.text_body}
          </p>
        </button>
      )}
      {expanded && (
        <>
          <div className="mx-[20px] border-t border-line" />
          <div className="px-[20px] py-3">
            <div className="text-sm leading-[1.6] text-fg space-y-2">
              <p className="whitespace-pre-wrap">{reply.text_body}</p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function ThreadMessageList({
  replies,
  actions,
}: {
  replies: Reply[]
  actions: ReplyActions
}) {
  const lastCountRef = useRef(replies.length)

  const scrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node && replies.length !== lastCountRef.current) {
        node.scrollTop = node.scrollHeight
        lastCountRef.current = replies.length
      }
    },
    [replies.length]
  )

  return (
    <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
      {replies.map((reply, index) => (
        <ThreadMessage
          key={reply.id}
          reply={reply}
          defaultExpanded={index === replies.length - 1}
          actions={actions}
        />
      ))}
    </div>
  )
}

type ComposeMode = 'reply' | 'forward'

function ReplyActionBar({
  reply,
  onCompose,
}: {
  reply: Reply
  onCompose: (mode: ComposeMode) => void
}) {
  const senderLabel = reply.sender_email_address?.split('@')[0] ?? 'outreach'

  return (
    <div className="px-[20px] py-4 border-t border-line">
      <div className="flex items-center gap-4 pl-[52px]">
        <button
          type="button"
          onClick={() => onCompose('forward')}
          className="inline-flex items-center gap-2 h-8 px-3 rounded border border-line text-sm text-fg hover:bg-surface-alt transition-colors"
        >
          <CornerUpRight className="size-4" />
          Forward
        </button>
        <button
          type="button"
          onClick={() => onCompose('reply')}
          className="inline-flex items-center gap-2 h-8 bg-brand-active text-fg-inverse px-3 rounded text-sm hover:bg-brand transition-colors"
        >
          <Send className="size-4" />
          Reply from @{senderLabel}
        </button>
      </div>
    </div>
  )
}

const composeSchema = z.object({
  body: z.string().min(1, 'Message body is required'),
})

type ComposeFormValues = z.infer<typeof composeSchema>

function ReplyComposePanel({
  reply,
  mode,
  onDiscard,
  onSent,
}: {
  reply: Reply
  mode: ComposeMode
  onDiscard: () => void
  onSent: () => void
}) {
  const replyMutation = useReplyToReply()
  const forwardMutation = useForwardReply()
  const [forwardTo, setForwardTo] = useState<
    { label: string; value: string }[]
  >([])

  const { control, handleSubmit } = useForm<ComposeFormValues>({
    defaultValues: { body: '' },
    resolver: zodResolver(composeSchema),
    mode: 'onSubmit',
  })

  const isSending = replyMutation.isPending || forwardMutation.isPending

  const onSubmit = handleSubmit((values) => {
    if (mode === 'reply') {
      const payload: ReplyToReplyPayload = { message: values.body }
      replyMutation.mutate(
        { replyId: reply.id, payload },
        { onSuccess: onSent }
      )
    } else {
      if (!forwardTo.length) return
      const payload: ForwardReplyPayload = {
        message: values.body,
        to_emails: forwardTo.map((opt) => ({
          name: opt.label === opt.value ? '' : opt.label,
          email_address: opt.value,
        })),
      }
      forwardMutation.mutate(
        { replyId: reply.id, payload },
        { onSuccess: onSent }
      )
    }
  })

  const sendButton = (
    <button
      type="button"
      onClick={onSubmit}
      disabled={isSending}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-3 rounded text-xs text-fg-inverse transition-colors',
        isSending
          ? 'bg-brand-active/60 cursor-not-allowed'
          : 'bg-brand-active hover:bg-brand'
      )}
    >
      <Send className="size-3" />
      {isSending ? 'Sending...' : 'Send'}
    </button>
  )

  const discardButton = (
    <button
      type="button"
      onClick={onDiscard}
      className="inline-flex items-center gap-1 h-7 px-2 rounded text-xs text-fg-secondary hover:bg-surface-alt transition-colors"
    >
      Discard
    </button>
  )

  return (
    <div className="border-t border-line">
      <div className="px-[20px] pt-3 pb-2">
        <p className="text-xs text-fg-secondary mb-2">
          {mode === 'reply'
            ? `Replying to ${reply.from_name || reply.from_email_address}`
            : 'Forward'}
        </p>
        {mode === 'forward' && (
          <div className="mb-2">
            <ReactCreatableSelectWrapper
              placeholder="Add recipients..."
              isMulti
              value={forwardTo}
              onChange={(val: unknown) =>
                setForwardTo((val ?? []) as { label: string; value: string }[])
              }
              onCreateOption={(input: string) => {
                const opt = { label: input, value: input }
                setForwardTo((prev) => [...prev, opt])
              }}
            />
          </div>
        )}
      </div>
      <div className="px-[20px] pb-4">
        <StyledEditorRHF
          name="body"
          control={control}
          placeholder="Write your message..."
          className="min-h-28 [&_.ql-container]:min-h-28 [&_.ql-editor]:min-h-28"
          toolbarPrefix={sendButton}
          toolbarSuffix={discardButton}
        />
      </div>
    </div>
  )
}

export function CampaignRepliesTab({ campaignId, className }: Props) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)
  const [selectedThreadContext, setSelectedThreadContext] = useState<{
    leadId: number | null
    campaignId: number | null
  }>({ leadId: null, campaignId: null })
  const [senderFilter, setSenderFilter] = useState<string[]>([])
  const [campaignFilter, setCampaignFilter] = useState<string[]>([])
  const [readFilter, setReadFilter] = useState<string[]>([])
  const [classificationFilter, setClassificationFilter] =
    useState<Classification>('all')
  const [composeMode, setComposeMode] = useState<ComposeMode | null>(null)

  const [debouncedSearch] = useDebounce(searchQuery, 300)

  // When campaignId is provided, resolve bisonCampaignId from the sequence
  const { data: sequence, isLoading: isLoadingSequence } =
    useGetCampaignSequence(campaignId ?? 0)
  const bisonCampaignId = campaignId
    ? (sequence?.bison_campaign_id ?? 0)
    : undefined

  // Base filters from the top-line dropdowns (used for both list and summary)
  const baseFilters = useMemo<ReplyFilters>(() => {
    const f: ReplyFilters = {}
    if (bisonCampaignId !== undefined) f.campaign_id = bisonCampaignId
    if (campaignFilter.length)
      f.campaign = campaignFilter.map((id) => Number(id))
    if (debouncedSearch) f.search = debouncedSearch
    if (senderFilter.length) f.sender_email = senderFilter
    if (readFilter.length === 1) f.is_read = readFilter[0] === 'read'
    return f
  }, [
    bisonCampaignId,
    campaignFilter,
    debouncedSearch,
    senderFilter,
    readFilter,
  ])

  // Full filters including classification pill (used for the list only)
  const filters = useMemo<ReplyFilters>(() => {
    const f = { ...baseFilters }
    if (classificationFilter === 'interested') f.is_interested = true
    if (classificationFilter === 'not_interested') f.is_interested = false
    if (classificationFilter === 'flagged') f.is_flagged = true
    return f
  }, [baseFilters, classificationFilter])

  const {
    data: repliesData,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetReplyThreadsInfinite(filters)
  const { data: summaryData } = useGetReplyThreadSummary(baseFilters)
  const { data: senderEmailsData } = useGetWorkspaceSenderEmails()
  const { data: campaignsData } = useGetCampaigns()

  const senderEmailSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'sender_emails',
        items: (senderEmailsData?.results ?? []).map((se) => ({
          label: se.name ? `${se.name} (${se.email})` : se.email,
          value: se.email,
        })),
      },
    ],
    [senderEmailsData]
  )

  const campaignSections = useMemo<SectionedSection[]>(
    () => [
      {
        key: 'campaigns',
        items: (campaignsData?.results ?? []).map((c) => ({
          label: c.name,
          value: String(c.id),
        })),
      },
    ],
    [campaignsData]
  )

  const markReplyRead = useMarkReplyRead()
  const toggleFlag = useToggleThreadFlag()
  const markInterested = useMarkReplyInterested()
  const markAutomated = useMarkReplyAutomated()
  const unsubscribeContact = useUnsubscribeReplyContact()
  const deleteReply = useDeleteReply()

  const replyActions: ReplyActions = useMemo(
    () => ({
      onMarkRead: (replyId, isRead) =>
        markReplyRead.mutate({ replyId, isRead }),
      onMarkAutomated: (replyId, isAutomated) =>
        markAutomated.mutate({ replyId, isAutomated }),
      onUnsubscribe: async (replyId) => {
        const ok = await confirm(
          'Are you sure you want to unsubscribe this contact? They will no longer receive emails from this campaign.',
          'Unsubscribe Contact'
        )
        if (ok) unsubscribeContact.mutate(replyId)
      },
      onDelete: async (replyId) => {
        const ok = await confirm(
          'This reply will be permanently deleted.',
          'Delete Reply',
          '',
          { confirmLabel: 'Delete', buttonVariant: 'danger' }
        )
        if (ok) deleteReply.mutate(replyId)
      },
    }),
    [markReplyRead, markAutomated, unsubscribeContact, deleteReply]
  )

  const replies = useMemo(
    () => repliesData?.pages.flatMap((p) => p.results) ?? [],
    [repliesData]
  )
  const currentSelectedReply = replies.find(
    (r) => r.thread_id === selectedThreadId
  )
  // Keep last valid selection during refetches so the UI doesn't flash blank
  const selectedReplyRef = useRef<Reply | undefined>(undefined)
  if (currentSelectedReply) {
    selectedReplyRef.current = currentSelectedReply
  } else if (!selectedThreadId) {
    selectedReplyRef.current = undefined
  }
  const selectedReply = currentSelectedReply ?? selectedReplyRef.current

  // Fetch all replies in the selected thread (uses stable context, not derived from selectedReply)
  const { data: threadData } = useGetThreadReplies(
    selectedThreadContext.leadId,
    selectedThreadContext.campaignId
  )
  const threadReplies = useMemo(
    () =>
      [...(threadData?.results ?? [])].sort(
        (a, b) =>
          new Date(a.date_received).getTime() -
          new Date(b.date_received).getTime()
      ),
    [threadData]
  )

  const { containerRef, sentinelRef } = useInfiniteScroll({
    onLoadMore: fetchNextPage,
    isLoading: isFetchingNextPage,
    hasMore: hasNextPage,
  })

  const handleSelectReply = useCallback(
    (reply: Reply) => {
      setSelectedThreadId(reply.thread_id)
      setSelectedThreadContext({
        leadId: reply.lead_id,
        campaignId: reply.campaign_id,
      })
      setComposeMode(null)
      if (!reply.is_read) {
        markReplyRead.mutate({ replyId: reply.id, isRead: true })
      }
    },
    [markReplyRead]
  )

  const selectedIndex = replies.findIndex(
    (r) => r.thread_id === selectedThreadId
  )

  const handlePrevReply = useCallback(() => {
    if (selectedIndex > 0) {
      handleSelectReply(replies[selectedIndex - 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const handleNextReply = useCallback(() => {
    if (selectedIndex < replies.length - 1) {
      handleSelectReply(replies[selectedIndex + 1])
    }
  }, [selectedIndex, replies, handleSelectReply])

  const hasActiveFilters =
    !!debouncedSearch ||
    senderFilter.length > 0 ||
    campaignFilter.length > 0 ||
    readFilter.length > 0 ||
    classificationFilter !== 'all'

  const formatFilterLabel = useCallback(
    (selected: string[], sections: SectionedSection[]) => {
      if (selected.length === 0) return 'All'
      if (selected.length <= 2) {
        return selected
          .map((val) => {
            for (const section of sections) {
              const item = section.items.find((i) => i.value === val)
              if (item) return item.label
            }
            return val
          })
          .join(', ')
      }
      return `${selected.length} selected`
    },
    []
  )

  // Only show full-page spinner on initial load, not on filter changes
  const isInitialLoad = isLoading && !repliesData
  const isWaitingForSequence = !!campaignId && isLoadingSequence

  if (isWaitingForSequence || isInitialLoad) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="size-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    )
  }

  // Empty state only when no filters are active and there are truly no replies
  if (replies.length === 0 && !hasActiveFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-3 rounded-full bg-surface-muted p-4">
          <MessageSquare className="size-8 text-fg-muted" />
        </div>
        <p className="text-base text-fg">No replies yet</p>
        <p className="mt-1 text-sm text-fg-secondary">
          Replies from your campaign contacts will appear here.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-4 flex-1 min-h-0', className)}>
      <div className="flex items-center gap-3 shrink-0">
        <div className="relative max-w-[360px] flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-muted" />
          <input
            type="text"
            placeholder="Search all replies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg bg-surface-muted py-2 pl-10 pr-4 text-sm text-fg outline-none placeholder:text-fg-muted focus:ring-2 focus:ring-brand/20"
          />
        </div>
        <SectionedMultiSelect
          buttonLabel="Sender"
          emptyLabel="Sender email"
          sections={senderEmailSections}
          selected={senderFilter}
          onApply={setSenderFilter}
          searchPlaceholder="Search senders..."
          formatLabel={formatFilterLabel}
        />
        {!campaignId && (
          <SectionedMultiSelect
            buttonLabel="Campaign"
            emptyLabel="Campaign"
            sections={campaignSections}
            selected={campaignFilter}
            onApply={setCampaignFilter}
            searchPlaceholder="Search campaigns..."
            formatLabel={formatFilterLabel}
          />
        )}
        <SectionedMultiSelect
          buttonLabel="Read status"
          emptyLabel="Read status"
          sections={READ_STATUS_SECTIONS}
          selected={readFilter}
          onApply={setReadFilter}
          searchable={false}
          formatLabel={formatFilterLabel}
        />
      </div>

      <ClassificationPills
        selected={classificationFilter}
        onChange={setClassificationFilter}
        summary={summaryData}
      />

      <div className="flex flex-1 min-h-0 gap-0 overflow-hidden rounded-lg border border-line bg-surface">
        <div
          ref={containerRef}
          className="w-96 shrink-0 overflow-y-auto border-r border-line"
        >
          {replies.length === 0 && !isFetching ? (
            <div className="flex items-center justify-center py-12 text-sm text-fg-muted">
              No replies match your filters
            </div>
          ) : (
            <>
              {replies.map((reply) => (
                <ReplyListItem
                  key={reply.id}
                  reply={reply}
                  isSelected={selectedThreadId === reply.thread_id}
                  onClick={() => handleSelectReply(reply)}
                />
              ))}
              <div ref={sentinelRef} style={{ height: '1px' }} />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-3">
                  <div className="size-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex flex-1 flex-col min-h-0 min-w-0 overflow-hidden">
          {selectedReply ? (
            <>
              <div className="shrink-0">
                <ReplyDetailHeader
                  reply={selectedReply}
                  currentIndex={selectedIndex}
                  totalCount={replies.length}
                  onPrev={handlePrevReply}
                  onNext={handleNextReply}
                />
              </div>
              {threadReplies.length > 1 ? (
                <ThreadMessageList
                  replies={threadReplies}
                  actions={replyActions}
                />
              ) : (
                <>
                  <ReplyContactHeader reply={selectedReply} />
                  <div className="mx-[20px] border-t border-line" />
                  <ReplyBodyText reply={selectedReply} />
                </>
              )}
              <ReplyClassificationRow
                reply={selectedReply}
                onToggleInterested={() => {
                  markInterested.mutate({
                    replyId: selectedReply.id,
                    isInterested: selectedReply.is_interested !== true,
                  })
                }}
                onToggleFlag={() => {
                  if (selectedReply.thread_id) {
                    toggleFlag.mutate(selectedReply.thread_id)
                  }
                }}
              />
              {composeMode ? (
                <ReplyComposePanel
                  reply={selectedReply}
                  mode={composeMode}
                  onDiscard={() => setComposeMode(null)}
                  onSent={() => setComposeMode(null)}
                />
              ) : (
                <ReplyActionBar
                  reply={selectedReply}
                  onCompose={setComposeMode}
                />
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-sm text-fg-muted">
              Select a reply to view details
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
